import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API_BASE_URL from "../services/apiClient";
import BackHeader from "../components/hub/BackHeader";

const TicketsPage = ({ onTabChange }) => {
  const { travelKeyToken } = useParams();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const emptyTicket = {
    type: "Flight",
    provider: "",
    departureDate: "",
    arrivalDate: "",
    notes: "",
    fileUrl: "",
  };

  const [newTicket, setNewTicket] = useState(emptyTicket);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchTickets = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          setTickets(data);
        }
      } catch (err) {
        console.error("Fetch tickets error:", err);
      } finally {
        setInitialLoaded(true);
        setLoading(false);
      }
    };

    fetchTickets();
  }, [travelKeyToken, navigate]);

  /* ================= AUTO SAVE ================= */
  useEffect(() => {
    if (!initialLoaded) return;

    const saveTickets = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      try {
        await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/tickets`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ tickets }),
        });
      } catch (err) {
        console.error("Save tickets error:", err);
      }
    };

    const timer = setTimeout(saveTickets, 1000);
    return () => clearTimeout(timer);
  }, [tickets, initialLoaded, travelKeyToken]);

  /* ================= ACTIONS ================= */
  const handleAddTicket = () => {
    if (!newTicket.provider) return;
    setTickets([...tickets, { ...newTicket, id: Date.now() }]);
    setNewTicket(emptyTicket);
    setShowAddForm(false);
  };

  const removeTicket = (id) => {
    setTickets(tickets.filter((t) => t.id !== id));
  };

  const getTransportIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "flight": return "✈️";
      case "train": return "🚂";
      case "bus": return "🚌";
      default: return "🎫";
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20">
        <div className="w-10 h-10 border-4 border-[#5b6f00] border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#6b7280]">Loading Tickets...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {onTabChange && (
        <BackHeader title="Tickets" onBack={() => onTabChange("menu")} />
      )}
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-1">Travel Tickets</h1>
          <p className="text-[#6b7280] font-medium">Manage your flight, train, and bus bookings.</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-[#5b6f00] text-white px-8 py-3.5 rounded-xl font-bold shadow-sm hover:bg-[#4a5a00] active:scale-95 transition-all flex items-center gap-2 w-fit"
        >
          <span className="text-lg">+</span> Add Ticket
        </button>
      </div>

      {/* Add Ticket Form Card */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm mb-10 overflow-hidden"
          >
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-bold">New Ticket Details</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-900 transition-colors"
                aria-label="Close form"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest px-1">Transport Type</label>
                <select
                  value={newTicket.type}
                  onChange={(e) => setNewTicket({ ...newTicket, type: e.target.value })}
                  className="w-full bg-[#fdfcf7] border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/10 transition-all font-bold"
                >
                  <option>Flight</option>
                  <option>Train</option>
                  <option>Bus</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest px-1">Provider Name</label>
                <input
                  placeholder="e.g. IndiGo, IRCTC"
                  value={newTicket.provider}
                  onChange={(e) => setNewTicket({ ...newTicket, provider: e.target.value })}
                  className="w-full bg-[#fdfcf7] border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/10 transition-all font-bold placeholder:text-gray-300"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest px-1">Departure</label>
                <input
                  type="datetime-local"
                  value={newTicket.departureDate}
                  onChange={(e) => setNewTicket({ ...newTicket, departureDate: e.target.value })}
                  className="w-full bg-[#fdfcf7] border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/10 transition-all font-bold"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest px-1">Arrival</label>
                <input
                  type="datetime-local"
                  value={newTicket.arrivalDate}
                  onChange={(e) => setNewTicket({ ...newTicket, arrivalDate: e.target.value })}
                  className="w-full bg-[#fdfcf7] border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/10 transition-all font-bold"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest px-1">Notes</label>
                <textarea
                  placeholder="Seat preference, gate info, etc."
                  value={newTicket.notes}
                  onChange={(e) => setNewTicket({ ...newTicket, notes: e.target.value })}
                  className="w-full bg-[#fdfcf7] border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/10 transition-all font-bold placeholder:text-gray-300 min-h-[100px]"
                />
              </div>

              {/* FILE URL INPUT */}
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest px-1">
                      Ticket File (URL)
                    </label>
                    <input
                      placeholder="Link to PDF or screenshot"
                      value={newTicket.fileUrl}
                      onChange={(e) => setNewTicket({ ...newTicket, fileUrl: e.target.value })}
                      className="w-full bg-[#fdfcf7] border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/10 transition-all font-bold placeholder:text-gray-300"
                    />
                  </div>

                  {/* 🔥 CLOUDINARY UPLOAD */}
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest px-1">
                      Or Upload Ticket (PDF / Image)
                    </label>

                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                            alert("Max 5MB file allowed");
                            return;
                          }

                        setUploading(true);

                        try {
                          const formData = new FormData();
                          formData.append("file", file);
                          formData.append("upload_preset", "expeditio_upload");

                          const res = await fetch(
                            "https://api.cloudinary.com/v1_1/dm5ifgtgw/auto/upload",
                            {
                              method: "POST",
                              body: formData,
                            }
                          );

                          const data = await res.json();

                          setNewTicket((prev) => ({
                            ...prev,
                            fileUrl: data.secure_url,
                          }));

                        } catch (err) {
                          console.error("Upload error:", err);
                        } finally {
                          setUploading(false);
                        }
                      }}
                      className="w-full bg-[#fdfcf7] border border-gray-100 rounded-xl px-4 py-3 font-bold"
                    />

                    {/* STATUS */}
                    {uploading && (
                      <p className="text-xs text-gray-500 font-bold">
                        ⏳ Uploading...
                      </p>
                    )}

                    {!uploading && newTicket.fileUrl && (
                      <p className="text-xs text-green-600 font-bold">
                        ✅ File ready
                      </p>
                    )}

                    {/* 🔥 PREVIEW */}
                    {newTicket.fileUrl && (
                      <a
                        href={newTicket.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#5b6f00] font-bold underline"
                      >
                        🔍 Preview ticket
                      </a>
                    )}
                  </div>
            </div>

            <div className="mt-10 flex justify-end gap-3">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-6 py-3 rounded-xl font-bold text-[#6b7280] hover:bg-[#fdfcf7] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTicket}
                className="bg-[#5b6f00] text-white px-8 py-3 rounded-xl font-bold shadow-sm hover:bg-[#4a5a00] active:scale-95 transition-all"
              >
                Save Ticket
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tickets List */}
      <div className="space-y-6">
        {tickets.length === 0 ? (
          <div className="bg-white rounded-2xl p-20 border-2 border-dashed border-gray-100 flex flex-col items-center text-center">
            <div className="text-6xl mb-6 opacity-20 grayscale">✈️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Access Your Orbit</h2>
            <p className="text-[#6b7280] mb-8 max-w-sm font-medium">
              Keep your logistics optimized. Add your first travel ticket to get started.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-[#fdfcf7] text-[#5b6f00] border border-[#5b6f00]/10 px-8 py-3 rounded-xl font-bold hover:bg-[#5b6f00]/5 transition-all"
            >
              + Create First Ticket
            </button>
          </div>
        ) : (
          
            
           tickets.map((ticket, index) => {
  const isPDF = ticket.fileUrl?.includes(".pdf");

  return (

            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              key={ticket.id || index}
              className="group bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:scale-[1.01] transition-all relative overflow-hidden"
            >
              <div className="flex flex-col md:flex-row justify-between gap-8">
                <div className="flex gap-6">
                  <div className="w-14 h-14 bg-[#fdfcf7] rounded-xl flex items-center justify-center text-3xl shrink-0 group-hover:rotate-3 transition-transform duration-500">
                    {getTransportIcon(ticket.type)}
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-[#5b6f00] tracking-widest mb-1">{ticket.type}</p>
                    <h3 className="text-xl font-bold text-gray-900 leading-tight mb-2">{ticket.provider}</h3>
                    {ticket.notes && (
                      <p className="text-sm text-[#6b7280] font-medium line-clamp-2 max-w-sm">{ticket.notes}</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col md:items-end justify-center border-t md:border-t-0 md:border-l border-gray-50 pt-6 md:pt-0 md:pl-8 gap-4 shrink-0">
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest">Departure</p>
                      <p className="text-sm font-bold text-gray-900">
                        {ticket.departureDate ? new Date(ticket.departureDate).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '---'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold uppercase text-[#6b7280] tracking-widest">Arrival</p>
                      <p className="text-sm font-bold text-gray-900">
                        {ticket.arrivalDate ? new Date(ticket.arrivalDate).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '---'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {ticket.fileUrl && (
                    <a
                      href={ticket.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs font-bold uppercase text-[#5b6f00] hover:underline transition-all"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {isPDF ? "📄 View PDF" : "🖼 View Image"}
                    </a>
                  )}
                </div>
                <button
                  onClick={() => removeTicket(ticket.id)}
                  className="p-3 text-gray-200 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  aria-label="Delete ticket"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </motion.div>
          );
})
        )}
      </div>
    </div>
  );
};

export default TicketsPage;
